'use client';

import { useState, useEffect } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Footer from '../components/Footer';
import { pricing } from '../config/pricing';
import { useRouter } from 'next/navigation';

export default function CheckoutPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [comingSoon] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Direct Stripe Checkout for static sites
  const handleDirectCheckout = () => {
    if (!isClient) return; // Safety check
    
    // Set loading state
    setLoading(true);
    
    // PRODUCTION: Replace this URL with your actual Stripe payment link
    // Create a payment link in the Stripe dashboard: https://dashboard.stripe.com/payment-links
    // Use the price ID for GhostHub OasiS ($169.00)
    const stripePaymentLink = process.env.NEXT_PUBLIC_STRIPE_PAYMENT_LINK || 'https://buy.stripe.com/test_8wM6qK6Xt9Ej1Gw000'; // Replace with your actual link
    
    // For static export, use direct window location change
    setTimeout(() => {
      window.location.href = stripePaymentLink;
    }, 500); // Small delay for better UX
  };
  
  useEffect(() => {
    // Mark that we're on the client side
    setIsClient(true);
    
    // Simple loading state management
    if (!comingSoon) {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } else {
      setLoading(false);
    }
    
    // Check if we came from a successful payment
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      handleOrderComplete({
        email: urlParams.get('email') || 'your.email@example.com',
        orderId: urlParams.get('order_id') || `GH-${Math.floor(Math.random() * 10000)}`
      });
    }
  }, [comingSoon]);

  // No Stripe Elements configuration needed for static site

  const handleOrderComplete = (details) => {
    setOrderDetails(details);
    setOrderComplete(true);
  };

  if (comingSoon) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 max-w-md mx-auto">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-ghost-600/20 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-ghost-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Coming Soon!</h3>
            <p className="text-gray-300 mb-4">I'm putting the finishing touches on the store and will be launching soon.</p>
            <p className="text-gray-400 text-sm mb-6">In the meantime, feel free to explore the website and learn more about GhostHub OasiS.</p>
            <div className="flex flex-col space-y-3">
              <a
                href="/"
                className="inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-ghost-600 hover:bg-ghost-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ghost-500"
              >
                Back to Home
              </a>
              <a
                href="https://github.com/BleedingXiko/GhostHub"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center px-6 py-3 border border-gray-700 text-base font-medium rounded-md shadow-sm text-gray-300 bg-dark-800 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ghost-500"
              >
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
                View on GitHub
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }



  if (orderComplete) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 p-8 bg-dark-800/50 rounded-xl border border-dark-700">
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                Order Confirmed!
              </h2>
              <p className="mt-2 text-center text-sm text-gray-400">
                Thank you for your purchase. I've sent a confirmation email to{' '}
                <span className="font-medium text-ghost-400">{orderDetails?.email}</span>.
              </p>
            </div>
            <div className="mt-8 border-t border-dark-700 pt-8">
              <h3 className="text-lg font-medium text-white">Order Summary</h3>
              <div className="mt-4 space-y-4">
                <div className="flex justify-between text-base">
                  <p className="text-gray-400">GhostHub OasiS</p>
                  <p className="text-white">{pricing.ghosthub.currency}{pricing.ghosthub.price}.00</p>
                </div>
                <div className="flex justify-between text-base">
                  <p className="text-gray-400">Shipping</p>
                  <p className="text-white">$20.00</p>
                </div>
                <div className="flex justify-between text-base font-medium text-white border-t border-dark-700 pt-4 mt-4">
                  <p>Total</p>
                  <p>$169.00</p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <a
                href="/"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-ghost-600 hover:bg-ghost-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ghost-500"
              >
                Back to Home
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
            <div className="lg:col-span-1">
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-extrabold text-white">Checkout</h1>
                <a 
                  href="/" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-ghost-600 hover:bg-ghost-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ghost-500"
                >
                  Back to Home
                </a>
              </div>
              
              <div className="mt-8">
                <h2 className="text-lg font-medium text-white">Order Summary</h2>
                <div className="mt-4 bg-dark-800/50 border border-dark-700 rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-16 w-16 rounded-md overflow-hidden">
                        <img 
                          src="/icons/Ghosthub192.png" 
                          alt="GhostHub Logo" 
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icons/Ghosthub.ico';
                          }}
                        />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-medium text-white">GhostHub OasiS</h3>
                          <p className="ml-4 text-base font-medium text-white">{pricing.ghosthub.currency}{pricing.ghosthub.price}.00</p>
                        </div>
                        <p className="mt-1 text-sm text-gray-400">Raspberry Pi 4 (2GB) with pre-installed GhostHub OS</p>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-dark-700 px-6 py-4 bg-dark-800/30">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Subtotal</p>
                      <p className="text-sm font-medium text-white">{pricing.ghosthub.currency}{pricing.ghosthub.price}.00</p>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-gray-400">Shipping</p>
                      <p className="text-sm font-medium text-white">$15.00</p>
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-dark-700 pt-4">
                      <p className="text-base font-medium text-white">Order total</p>
                      <div className="text-right">
                        <p className="text-base font-bold text-white">{pricing.ghosthub.currency}{pricing.ghosthub.price + 15}.00*</p>
                        <p className="text-xs text-gray-400">*Final price calculated on next page</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 lg:mt-0">
              <h2 className="text-lg font-medium text-white mb-6">Shipping & Payment</h2>
              
              {!comingSoon && (
                <div className="bg-dark-800/50 border border-dark-700 rounded-lg p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-white">Shipping Information</h3>
                      <p className="mt-1 text-sm text-gray-400">You'll enter your shipping details on the next page.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium text-white">Payment</h3>
                      <p className="mt-1 text-sm text-gray-400">Secure payment processing by Stripe.</p>
                      <div className="mt-4 flex flex-col items-center space-y-3">
                        <span className="text-sm text-gray-400">Secure payment processing</span>
                        <img 
                          src="https://cdn.brandfolder.io/KGT2DTA4/at/rvgw5pc69nhv9wkh7rw8ckv/Powered_by_Stripe_-_blurple.svg" 
                          alt="Powered by Stripe" 
                          className="h-7" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icons/Ghosthub192.png';
                          }}
                        />
                      </div>
                    </div>
                    
                    <button
                      onClick={handleDirectCheckout}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-ghost-600 hover:bg-ghost-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ghost-500"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        'Continue to Payment'
                      )}
                    </button>
                    
                    <p className="text-xs text-center text-gray-400 mt-4">
                      By completing your purchase, you agree to my <a href="https://stripe.com/legal/ssa" target="_blank" rel="noopener noreferrer" className="text-ghost-400 hover:text-ghost-300">Terms of Service</a> and <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-ghost-400 hover:text-ghost-300">Privacy Policy</a>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
